import { SortingDirection } from 'enums';
import qs from 'qs';
import { omitBy } from 'lodash';
import {
  AccountStatus,
  ProgressTrackerGroup,
  ProgressTrackerGroupRequest,
  ProgressTrackerGroups,
  ProgressTrackerStages,
  ProgressTrackerStatus,
  Status,
  IAccountRequest,
  IAccountListItem,
  IAccount,
  IAccountBase,
  AccountStatusLabel,
} from 'models/account';
import { ApiListResp, PageInfo } from 'models/api';
import { defaultTablePageSize } from 'constants/common';
import { apiV2 } from './axios';

export interface GetAccountsProps {
  page?: number;
  size?: number;
  status?: AccountStatusLabel;
  search?: string;
  from?: string;
  to?: string;
  sortingName: string;
  sortingDirection?: SortingDirection;
  taxLiabilityStatus?: string;
  taxLiabilityYear?: string;
}

export interface GetAccountsListProps {
  page?: number;
  size?: number;
  status?: string;
  search?: string;
  from?: string;
  to?: string;
  sortingName: string;
  sortingDirection?: SortingDirection;
  taxLiabilityStatus: string;
  taxLiabilityYear: string;
  entityType: string;
  documentNotes: string;
  role?: string;
  csm: string;
  state: string;
  lastLoginDate: string;
  stateOfIncorporation: string;
  plan: string;
  xeroBroken?: boolean;
  oilPendingByTaxType?: (string | null)[];
  bookkeeper?: string;
  formationsPlan?: string;
  dataSource?: string;
  bankConnectionStatus?: string;
  payrollOnboarding?: string;
}

export interface IMetadata {
  total: number;
  types: Array<{
    label: AccountStatusLabel;
    count: number;
  }>;
}

export type ProgressTrackerGroupResp = Omit<
  ProgressTrackerGroupRequest,
  'status'
> & {
  status: Status[];
};

// const setDefaultValueForIncomeExpense = (account: IAccountResp) =>
//   account?.incomeExpense || {
//     income: 0,
//     associatedWithBrokerage: false,
//     categoryExpense: {},
//   };

// // set default value for some attributes in account
// const transformAccountRespData = (account: IAccountResp): IAccount => ({
//   ...account,
//   // entityType: account.entityType,
//   status: account.status,
//   // incomeExpense: setDefaultValueForIncomeExpense(account),
//   payrollEnabled: account?.payrollEnabled ?? false,
//   payrollRunNumber: account?.payrollRunNumber ?? 0,
//   companyName: account?.companyName ?? '',
//   payroll: account?.payroll ?? {
//     estimatedSalary: 0,
//     ytdOfficerWages: 0,
//     ytdNonOfficerWages: 0,
//     lyOfficerWages: 0,
//     lyNonOfficerWages: 0,
//   },
//   progress: account?.progress
//     ? account.progress.map((prog: ProgressTrackerGroupResp) => ({
//         ...prog,
//         status: prog.status,
//       }))
//     : [], // progress will be null when progress tracker have no step
//   taxes: {
//     annualEstimated: account?.taxes?.annualEstimated || 0,
//     ytdTotalPaid: account?.taxes?.ytdTotalPaid || 0,
//     updatedAt: account?.taxes?.updatedAt,
//   },
//   xeroTransactionsError: account.xeroTransactionsError ?? [],
//   xeroLastUpdatedAt: account?.xeroLastUpdatedAt,
//   xeroLastUpdatedAtTransactions: account?.xeroLastUpdatedAtTransactions,
//   createdAt: account?.createdAt,

//   healthInsurance: 'todo', // to remove along with business health
//   retirementPlan: 'todo', // to remove along with business health

//   oilUnreadCustomerNoteItemsCount: account?.oilUnreadCustomerNoteItemsCount,
//   oilPendingReviewItemsCount: account?.oilPendingReviewItemsCount,
//   formationsPlan: account?.formationsPlan ?? FormationsPlan.FullSuite, // default to full suite,
//   generalTaxInfo: account?.generalTaxInfo,
//   homeAddress: account?.homeAddress,
// });

export const getAccountListByUserId = (userId: string): Promise<IAccount[]> =>
  apiV2
    .get<{ data: IAccount[] }>(`accounts/by-user/${userId}`)
    .then((resp) => resp.data as unknown as IAccount[]);

export const getAccount = (id?: string): Promise<IAccount> =>
  apiV2.get<IAccount>(`accounts/${id}`).then((resp) => resp.data);

export const getPrimaryAccountByUserId = (userId: string): Promise<IAccount> =>
  apiV2
    .get<{ data: IAccount }>(`accounts/by-owner/${userId}`)
    .then((resp) => resp.data.data as unknown as IAccount);

export const updateAccountById = (
  id: string,
  params: Partial<IAccountRequest>,
) => apiV2.patch(`accounts/${id}`, params);

export const getAccountsMeta = async () =>
  apiV2
    .get<IMetadata>('/accounts/meta')
    .then((resp) => resp.data);

export const updateStatus = async (id: string, label: AccountStatusLabel) => {
  const { data } = await apiV2.patch<{ label: AccountStatusLabel }>(
    `/accounts/${id}`,
    {
      label,
    },
  );
  return data;
};

/** Get account service */
const getSortingName = (tableHeaderName: string) => {
  let name = '';

  switch (tableHeaderName) {
    case 'dayInStatus':
      name = 'dayInStatus';
      break;
    case 'registeredDate':
      name = 'created_at';
      break;
    case 'companyName':
      name = 'company_name';
      break;
    case 'ownerName':
      name = 'owner_name';
      break;
    default:
      break;
  }

  return name;
};

export type IAccountListItemResp = Omit<IAccountListItem, 'progress'> & {
  progress: ProgressTrackerGroupResp[];
};
export type ApiListAccountResp = ApiListResp<IAccountListItemResp[]>;

export const defaultPageInfo: PageInfo = {
  currentPage: 1,
  nextPage: null,
  pageCount: 1,
  pageSize: defaultTablePageSize,
  prevPage: null,
  totalCount: 0,
};
export const getAccounts = ({
  page = 1,
  size = 20,
  status,
  search,
  from,
  to,
  sortingName,
  sortingDirection = SortingDirection.Asc,
  taxLiabilityStatus,
  taxLiabilityYear,
}: GetAccountsProps) => {
  const params = omitBy(
    {
      status,
      keyword: search,
      from,
      to,
      taxLiabilityStatus,
      taxLiabilityYear,
      sort: getSortingName(sortingName),
      order: sortingDirection,
      page,
      size,
    },
    (value) => value == null || value === '',
  );

  return apiV2
    .get<ApiListAccountResp>(`/accounts/search`, { params })
    .then((resp) => resp.data);
};

const transformAccountList = (
  account: IAccountListItemResp,
): IAccountListItem => ({
  ...account,
  createdAt: account.createdAt,
  progress: account.progress
    ? account.progress.map((prog: ProgressTrackerGroupResp) => ({
        ...prog,
        status: prog.status,
      }))
    : [],
});

export const getAccountsList = async ({
  page = 1,
  size = 20,
  status,
  search,
  from,
  to,
  sortingName,
  sortingDirection = SortingDirection.Asc,
  taxLiabilityStatus,
  taxLiabilityYear,
  entityType,
  documentNotes,
  role,
  csm,
  state,
  lastLoginDate,
  stateOfIncorporation,
  plan,
  xeroBroken,
  oilPendingByTaxType,
  bookkeeper,
  formationsPlan,
  dataSource,
  bankConnectionStatus,
  payrollOnboarding,
}: GetAccountsListProps) => {
  const params = omitBy(
    {
      status,
      keyword: search,
      from,
      to,
      taxLiabilityStatus,
      taxLiabilityYear,
      sort: getSortingName(sortingName),
      order: sortingDirection,
      page,
      size,
      entityType,
      documentNotes,
      role,
      csm,
      state,
      lastLoginDate,
      stateOfIncorporation,
      plan,
      xeroBroken,
      oilPendingByTaxType,
      bookkeeper,
      formationsPlan,
      dataSource,
      bankConnectionStatus,
      payrollOnboarding,
    },
    (value) => value == null || value === '',
  );

  const resp = await apiV2.get<ApiListAccountResp>(`/accounts`, {
    params,
    paramsSerializer: (queryParams) =>
      qs.stringify(queryParams, { arrayFormat: 'repeat' }),
  });
  return {
    data: resp.data.data?.map(transformAccountList),
    pageInfo: resp.data.pageInfo,
  } as ApiListResp<IAccountListItem[]>;
};

export const AccountService = {
  getAccount,
  updateAccountById,
};

// we are only returning email for now, in the future we might need to return more info

export const getCSMList = async (): Promise<string[]> => {
  const { data } = await apiV2.get<string[]>('accounts/csms');
  return data;
};

export type {
  AccountStatus,
  IAccount,
  ProgressTrackerGroup,
  ProgressTrackerGroupRequest,
  Status,
};

export type BookkeeperOption = {
  id: string;
  name: string;
};
export const getBookkeepersList = async (): Promise<BookkeeperOption[]> => {
  const { data } = await apiV2.get<BookkeeperOption[]>('accounts/bookkeepers');
  return data;
};

export { ProgressTrackerGroups, ProgressTrackerStages, ProgressTrackerStatus };

export const activateAccount = async (accountId: string) => {
  const { data } = await apiV2.post<{ data: IAccount }>(
    `accounts/${accountId}/activate`,
  );
  return data;
};
